import { DatePicker } from '@mui/x-date-pickers-pro';
import { useRef, useEffect } from 'react';
import { useIntl, IntlShape } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  ComboBox,
  Dropdown,
  type DropdownListItem,
  TextField,
} from '@verantos-dev/shared-components';
import {
  InputFieldTypeLegacy,
  type InputFieldConfig,
  type InputFieldLegacy,
} from '@verantos-dev/types-cohorts';

const getValueAndFieldError = (
  propValue: string | undefined,
  options: DropdownListItem[] | undefined,
  multiple: boolean,
  fieldErrorText?: string,
  intl?: IntlShape
): { value: DropdownListItem[] | DropdownListItem | undefined; fieldError: string | undefined } => {
  const value = multiple
    ? propValue !== ''
      ? propValue
          ?.split(',')
          .map((item) =>
            options?.find((option) => [option.value, option.displayValue].includes(item))
          )
          .filter((item): item is DropdownListItem => item !== undefined)
      : []
    : (options?.find((option) => option.value === propValue) as DropdownListItem) ?? undefined;

  const isValueAvailable = Array.isArray(value) ? value.length > 0 : !!value;
  const fieldError = isValueAvailable
    ? undefined
    : intl &&
      intl.formatMessage(
        {
          id: 'projects.cohortBuilderPage.inputFieldError',
        },
        { field: fieldErrorText ?? 'field' }
      );

  return { value, fieldError };
};

export type CohortBuilderInputFieldProps = {
  onChange: (value: string) => void;
  readOnly?: boolean;
  viewMode?: boolean;
  multiple?: boolean;
  showFieldError?: boolean;
  updateFieldError?: (id: string, error?: string) => void; // callback to handle field error state
  fieldErrorText?: string; // field error text to display
  showCheckboxesInCombobox?: boolean;
  showIconsInCombobox?: boolean;
} & InputFieldLegacy &
  InputFieldConfig;

export const CohortBuilderInputField = ({
  inputType,
  id,
  width = 'medium',
  label,
  placeholder,
  helperText,
  options,
  value: propValue,
  minValue = 0,
  minValueMsg,
  maxValue,
  maxValueMsg,
  disabled,
  readOnly,
  onChange,
  viewMode = false,
  multiple = false,
  showFieldError = false,
  updateFieldError, // This is used to handle the parent field error state
  fieldErrorText, // This is used for the field error text display
  showCheckboxesInCombobox = false,
  showIconsInCombobox = false,
}: CohortBuilderInputFieldProps) => {
  /** Handles local state to allow changing enterable fields without firing runtime,
   * runtime triggers will occur on blur by calling the passed in onChange rather than the local state value change */

  const intl = useIntl();
  /** Called onBlur text inputs or onChange for select inputs */
  let fieldError: string | undefined;
  let value;
  const restrictSaveProcess = useFeatureFlagSplit('restrict_save_process_on_error', false);
  const previousFieldError = useRef<string | undefined>(undefined);

  const onSubmitToRuntime = (_selectedValue?: string) => {
    if (!disabled) {
      const value = options?.find((option) => option.displayValue === _selectedValue)?.value;
      onChange(_selectedValue ?? value ?? '');
    }
  };

  if (
    restrictSaveProcess &&
    (inputType === InputFieldTypeLegacy.CONCEPT_COMBO_BOX ||
      inputType === InputFieldTypeLegacy.COMBO_BOX)
  ) {
    const result = getValueAndFieldError(propValue, options, multiple, fieldErrorText, intl);
    value = result.value;
    fieldError = result.fieldError;
  }
  // Notify parent about fieldError changes
  useEffect(() => {
    if (restrictSaveProcess && updateFieldError && fieldError !== previousFieldError.current) {
      updateFieldError(id, fieldError);
      previousFieldError.current = fieldError;
    }
  }, [id, fieldError, updateFieldError, restrictSaveProcess]);

  switch (inputType) {
    case InputFieldTypeLegacy.CHECKBOX:
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    case InputFieldTypeLegacy.CONCEPT_COMBO_BOX:
    case InputFieldTypeLegacy.COMBO_BOX: {
      if (!restrictSaveProcess) {
        value = multiple
          ? propValue !== ''
            ? propValue
                ?.split(',')
                .map((item) =>
                  options?.find((option) => [option.value, option.displayValue].includes(item))
                )
                .filter((item): item is DropdownListItem => item !== undefined)
            : []
          : (options?.find((option) => option.value === propValue) as DropdownListItem) ??
            undefined;
      }

      return (
        <ComboBox
          id={id}
          width={width}
          label={label}
          placeholder={placeholder}
          options={options || []}
          value={value}
          showCheckbox={showCheckboxesInCombobox}
          showIcon={showIconsInCombobox}
          onChange={(_, value) => {
            if (Array.isArray(value)) {
              onChange(
                value
                  .map(
                    (item) => options?.find((option) => option.value === item.value)?.value ?? ''
                  )
                  .join(',')
              );
            } else {
              onChange(value?.value ?? '');
            }
          }}
          readOnly={readOnly}
          viewMode={viewMode}
          multiple={multiple}
          fieldError={showFieldError ? fieldError : undefined}
        />
      );
    }

    case InputFieldTypeLegacy.DROPDOWN: {
      return (
        <Dropdown
          id={id}
          width={width}
          label={label}
          placeholder={placeholder}
          options={options}
          value={propValue}
          onChange={(event) => onSubmitToRuntime(event.target.value as string)}
          readOnly={readOnly}
          viewMode={viewMode}
        />
      );
    }

    case InputFieldTypeLegacy.DATE_PICKER: {
      return <DatePicker label="Date" value={propValue} />;
    }

    case InputFieldTypeLegacy.FLOAT_NUMERIC_FIELD: {
      return (
        <TextField
          id={id}
          type="float"
          maxDigitsNumeric={8}
          maxDigitsExponent={3}
          minNumericValue={minValue}
          maxNumericValue={maxValue}
          beepMaxDigits={true}
          showErrorMsg={true}
          width={width}
          displayLabel={label}
          placeholder={placeholder}
          helperText={helperText}
          value={propValue as string}
          onChange={(_, value) => onSubmitToRuntime(value)}
          readOnly={readOnly}
          viewMode={viewMode}
        />
      );
    }
    case InputFieldTypeLegacy.INT_NUMERIC_FIELD:
      return (
        <TextField
          id={id}
          type="integer"
          maxDigitsNumeric={8}
          minNumericValue={minValue}
          minNumericValueErrorMsg={minValueMsg}
          maxNumericValue={maxValue}
          beepMaxDigits={true}
          showErrorMsg={true}
          width={width}
          displayLabel={label}
          placeholder={placeholder}
          helperText={helperText}
          value={propValue as string}
          onChange={(_, value) => onSubmitToRuntime(value)}
          readOnly={readOnly}
          viewMode={viewMode}
        />
      );

    case InputFieldTypeLegacy.TEXT_FIELD: {
      return (
        <TextField
          id={id}
          width={width}
          displayLabel={label}
          placeholder={placeholder}
          helperText={helperText}
          value={propValue as string}
          onChange={(_, value) => onSubmitToRuntime(value)}
          readOnly={readOnly}
          viewMode={viewMode}
        />
      );
    }
  }
};
